// Enhanced hook for detecting both role and stream from profiles table
// This aligns with the BackOn Developer & Designer Playbook strategy

import { supabase } from "@/lib/supabaseClient";
import { useEffect, useState } from "react";
// Note: use dynamic import for enterprise role detection to avoid
// mixed static/dynamic imports and potential circular init issues

/**
 * Helper function to determine stream from role
 */
function getStreamFromRole(roleOrStream: string): string {
  const value = roleOrStream || "";
  const roleUpper = value.toUpperCase();
  const valueLower = value.toLowerCase();

  // If value already looks like a stream id, normalize and return
  if (["individual", "family", "business", "assisted", "assisted_care"].includes(valueLower)) {
    return valueLower === "assisted" ? "assisted_care" : valueLower;
  }

  // Admin roles
  if (
    roleUpper === "ADMIN" ||
    roleUpper === "BACKON_ADMIN" ||
    roleUpper === "BACKON_SUPPORT" ||
    roleUpper === "OWNER"
  ) {
    return "admin";
  }

  // Family stream roles
  if (
    roleUpper === "PARENT" ||
    roleUpper === "CHILD" ||
    roleUpper === "COACH"
  ) {
    return "family";
  }

  // Business stream roles
  if (
    roleUpper === "BUSINESS_OWNER" ||
    roleUpper === "TEAM_ADMIN" ||
    roleUpper === "TEAM_MEMBER"
  ) {
    return "business";
  }

  // Assisted care stream roles
  if (
    roleUpper === "ELDER" ||
    roleUpper === "CARE_STAFF" ||
    roleUpper === "FAMILY_PROXY" ||
    roleUpper === "SENIOR"
  ) {
    return "assisted_care";
  }

  // Default to individual
  return "individual";
}

export function useMyStreamAndRole() {
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<string | null>(null);
  const [stream, setStream] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [my, setMy] = useState<any>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [active_stream, setActiveStream] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout;

    (async () => {
      try {
        console.log("🔍 useMyStreamAndRole: Starting profile fetch...");

        const { data: sess } = await supabase.auth.getSession();
        const uid = sess?.session?.user?.id;

        console.log("🔍 useMyStreamAndRole: User ID:", uid);

        if (!uid) {
          console.log(
            "🔍 useMyStreamAndRole: No user ID found, setting loading to false",
          );
          if (mounted) setLoading(false);
          return;
        }

        // Set a 1.5 second timeout fallback
        timeoutId = setTimeout(() => {
          if (mounted && loading) {
            console.log(
              "⏰ useMyStreamAndRole: Timeout reached, setting null values for stream selection",
            );
            setRole(null);
            setStream(null);
            setActiveStream(null);
            setLoading(false);
          }
        }, 1500);

        // Query the actual schema columns that exist
        let { data, error } = await supabase
          .from("profiles")
          .select("id, app_role, display_name, user_id, org_id, parent_id")
          .eq("user_id", uid)
          .maybeSingle();

        console.log("🔍 useMyStreamAndRole: Profile query result:", {
          data,
          error,
        });

        if (!mounted) return;

        // Clear the timeout since we got a response
        clearTimeout(timeoutId);

        if (error) {
          console.warn("⚠️ useMyStreamAndRole: Error fetching profile:", error);
          setError(error.message);
          // Set null values on error to trigger stream selection
          setRole(null);
          setStream(null);
          setActiveStream(null);
        } else if (data) {
          console.log("✅ useMyStreamAndRole: Profile found:", data);
          
          // Get stream from user metadata if available
          const userMetadata = sess.session?.user?.user_metadata || {};
          const detectedRole = data.app_role || "individual";
          // app_role may already be a stream id; prefer metadata.stream, then normalize app_role
          const detectedStream = userMetadata.stream || getStreamFromRole(detectedRole);
          const detectedActiveStream = detectedStream;

          setRole(detectedRole);
          setStream(detectedStream);
          setActiveStream(detectedActiveStream);
          setMy({
            ...data,
            role: detectedRole,
            stream: detectedStream,
            active_stream: detectedActiveStream,
            full_name: data.display_name,
            email: sess.session?.user?.email
          });
          setOrgId(data.org_id || null);
        } else {
          console.log(
            "🔍 useMyStreamAndRole: No profile found, creating with enterprise role detection...",
          );

          // Use enterprise role detection to determine the correct role and stream
          const userEmail = sess.session.user.email || "";
          const userMetadata = sess.session.user.user_metadata || {};
          const { detectEnterpriseRole } = await import(
            "@/utils/enterpriseRoleDetection"
          );
          const enterpriseRole = detectEnterpriseRole(userEmail, userMetadata);

          console.log(
            "🔍 useMyStreamAndRole: Enterprise role detected:",
            enterpriseRole,
          );

          // Profile doesn't exist, create it with the detected role
          const { error: insertError } = await supabase
            .from("profiles")
            .insert({
              user_id: uid,
              app_role: enterpriseRole.role,
              display_name: sess.session.user.user_metadata?.full_name ||
                sess.session.user.email?.split("@")[0] ||
                "User",
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });

          if (insertError) {
            console.error(
              "❌ useMyStreamAndRole: Error creating profile:",
              insertError,
            );
            setError(insertError.message);
            // Set null values on insert error to trigger stream selection
            setRole(null);
            setStream(null);
            setActiveStream(null);
          } else {
            console.log(
              "✅ useMyStreamAndRole: Enterprise profile created successfully",
            );
            // Set the detected role and stream
            const detectedStream = getStreamFromRole(enterpriseRole.role);
            setRole(enterpriseRole.role);
            setStream(detectedStream);
            setActiveStream(detectedStream);
            setMy({
              id: uid,
              role: enterpriseRole.role,
              stream: getStreamFromRole(enterpriseRole.role),
              full_name: sess.session.user.user_metadata?.full_name ||
                sess.session.user.email?.split("@")[0] ||
                "User",
              email: sess.session.user.email,
            });
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("❌ useMyStreamAndRole: Unexpected error:", err);
        if (mounted) {
          clearTimeout(timeoutId);
          setLoading(false);
          setError(err instanceof Error ? err.message : "Unknown error");
          // Set null values as fallback to trigger stream selection
          setRole(null);
          setStream(null);
          setActiveStream(null);
        }
      }
    })();

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const refresh = () => {
    setLoading(true);
    setError(null);
    // This will trigger the useEffect to run again
  };

  return {
    loading,
    role,
    stream,
    error,
    my,
    orgId,
    active_stream,
    refresh,
  };
}

/**
 * Hook for getting just the role (for backward compatibility)
 */
export function useMyRole() {
  const { role, loading, error } = useMyStreamAndRole();
  return { role, loading, error };
}

/**
 * Hook for getting just the stream (for backward compatibility)
 */
export function useMyStream() {
  const { stream, loading, error } = useMyStreamAndRole();
  return { stream, loading, error };
}

/**
 * Hook for getting active stream (for stream switching)
 */
export function useMyActiveStream() {
  const { active_stream, loading, error, refresh } = useMyStreamAndRole();
  return { active_stream, loading, error, refresh };
}
